import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Reveals a complete string progressively, one small chunk at a time.
 *
 * The assistant API returns the full answer in one POST, so there is no server
 * stream to read from. This hook simulates streaming on the client: it starts
 * from an empty string and appends chunks of `text` on an interval until the
 * whole answer is shown, then calls `onComplete`.
 *
 * - `text` changes (a new answer) restart the reveal from zero.
 *   `speedMs` is the pause between chunks — smaller feels faster.
 *   `chunkSize` is how many characters are revealed at once.
 *
 * Returns the revealed prefix, whether the reveal is still running, and a
 * `stop` that finishes the reveal immediately (used when the person navigates
 * away or starts a new conversation).
 */
export function useStreamingText({
  text,
  speedMs = 12,
  chunkSize = 6,
  onComplete,
}: {
  /** The complete answer to reveal. */
  text: string;
  /** Milliseconds between revealed chunks. */
  speedMs?: number;
  /** Characters revealed per tick. */
  chunkSize?: number;
  /** Called once the full text has been revealed. */
  onComplete?: () => void;
}) {
  const [revealed, setRevealed] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setIsStreaming(false);
    if (revealed !== text) {
      setRevealed(text);
    }
    void onComplete?.();
  }, [revealed, text, onComplete]);

  const lastRevealedText = useRef<string | undefined>(undefined);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  useEffect(() => {
    // Only restart when the source text changes, not on every revealed tick.
    if (lastRevealedText.current === text) return;
    lastRevealedText.current = text;

    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (text === '') return;

    // Defer the state reset so it is not synchronous in the effect body
    // (which would cascade renders). The first tick follows immediately after.
    setTimeout(() => {
      setRevealed('');
      setIsStreaming(true);
    }, 0);

    let index = 0;
    const tick = () => {
      const next = text.slice(0, index + chunkSize);
      setRevealed(next);
      index += chunkSize;

      if (index >= text.length) {
        timer.current = null;
        setIsStreaming(false);
        void onComplete?.();
        return;
      }

      timer.current = setTimeout(tick, speedMs);
    };

    timer.current = setTimeout(tick, speedMs);
  }, [text, speedMs, chunkSize, onComplete]);

  return { revealed, isStreaming, stop };
}
